export const PROTECTED_CACHE_CLEAR_REQUEST =
  "WHITEHAVEN_CLEAR_PROTECTED_CACHES";
export const PROTECTED_CACHE_CLEAR_RESPONSE =
  "WHITEHAVEN_PROTECTED_CACHES_CLEARED";
export const PROTECTED_CACHE_CLEAR_PROTOCOL_VERSION = 1;
export const DEFAULT_SERVICE_WORKER_TIMEOUT_MS = 4_000;
const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export type ServiceWorkerCleanupCode =
  | "SERVICE_WORKER_UNAVAILABLE"
  | "SERVICE_WORKER_POST_FAILED"
  | "SERVICE_WORKER_TIMEOUT"
  | "SERVICE_WORKER_RESPONSE_INVALID"
  | "CACHE_CLEANUP_FAILED";

export type ServiceWorkerCleanupResult =
  | { readonly status: "cleared" }
  | {
      readonly status: "skipped" | "failed";
      readonly code: ServiceWorkerCleanupCode;
    };

interface MessagePortLike {
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  start?: () => void;
  close: () => void;
}

interface MessageChannelLike {
  readonly port1: MessagePortLike;
  readonly port2: MessagePortLike;
}

interface ServiceWorkerControllerLike {
  postMessage(message: unknown, transfer: readonly unknown[]): void;
}

export interface ProtectedCacheClearRequestOptions {
  readonly controller?: ServiceWorkerControllerLike | null;
  readonly createMessageChannel?: () => MessageChannelLike;
  readonly timeoutMs?: number;
  readonly createRequestId?: () => string;
  readonly scheduleTimeout?: (
    callback: () => void,
    timeoutMs: number,
  ) => ReturnType<typeof setTimeout>;
  readonly cancelTimeout?: (timeoutId: ReturnType<typeof setTimeout>) => void;
}

let fallbackRequestSequence = 0;

function createDefaultRequestId(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  fallbackRequestSequence += 1;
  return `request-${fallbackRequestSequence}`;
}

function resolveController(): ServiceWorkerControllerLike | null {
  try {
    if (
      typeof navigator === "undefined" ||
      !("serviceWorker" in navigator) ||
      !navigator.serviceWorker.controller
    ) {
      return null;
    }

    return navigator.serviceWorker.controller;
  } catch {
    return null;
  }
}

function resolveMessageChannel(): MessageChannelLike | null {
  if (typeof MessageChannel === "undefined") {
    return null;
  }

  return new MessageChannel();
}

function isResponse(
  value: unknown,
  requestId: string,
): value is {
  type: typeof PROTECTED_CACHE_CLEAR_RESPONSE;
  version: typeof PROTECTED_CACHE_CLEAR_PROTOCOL_VERSION;
  requestId: string;
  ok: boolean;
  code?: "CACHE_CLEANUP_FAILED";
} {
  if (!value || typeof value !== "object") {
    return false;
  }

  const response = value as Record<string, unknown>;
  const allowedKeys = new Set(["type", "version", "requestId", "ok", "code"]);

  return (
    Object.keys(response).every((key) => allowedKeys.has(key)) &&
    response.type === PROTECTED_CACHE_CLEAR_RESPONSE &&
    response.version === PROTECTED_CACHE_CLEAR_PROTOCOL_VERSION &&
    response.requestId === requestId &&
    typeof response.ok === "boolean" &&
    ((response.ok === true && response.code === undefined) ||
      (response.ok === false && response.code === "CACHE_CLEANUP_FAILED"))
  );
}

export function requestProtectedCacheClear(
  options: ProtectedCacheClearRequestOptions = {},
): Promise<ServiceWorkerCleanupResult> {
  const controller =
    "controller" in options
      ? (options.controller ?? null)
      : resolveController();

  if (!controller) {
    return Promise.resolve({
      status: "skipped",
      code: "SERVICE_WORKER_UNAVAILABLE",
    });
  }

  let channel: MessageChannelLike | null;
  let requestId: string;

  try {
    channel = options.createMessageChannel?.() ?? resolveMessageChannel();
    requestId = (options.createRequestId ?? createDefaultRequestId)();
  } catch {
    return Promise.resolve({
      status: "failed",
      code: "SERVICE_WORKER_POST_FAILED",
    });
  }

  if (!channel || !REQUEST_ID_PATTERN.test(requestId)) {
    return Promise.resolve({
      status: channel ? "failed" : "skipped",
      code: channel
        ? "SERVICE_WORKER_POST_FAILED"
        : "SERVICE_WORKER_UNAVAILABLE",
    });
  }

  const timeoutMs = Math.max(
    1,
    Math.min(
      typeof options.timeoutMs === "number" &&
        Number.isFinite(options.timeoutMs)
        ? options.timeoutMs
        : DEFAULT_SERVICE_WORKER_TIMEOUT_MS,
      DEFAULT_SERVICE_WORKER_TIMEOUT_MS,
    ),
  );
  const scheduleTimeout = options.scheduleTimeout ?? setTimeout;
  const cancelTimeout = options.cancelTimeout ?? clearTimeout;

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const finish = (result: ServiceWorkerCleanupResult) => {
      if (settled) {
        return;
      }

      settled = true;
      try {
        if (timeoutId !== null) {
          cancelTimeout(timeoutId);
        }
      } catch {
        // Timer cleanup must not prevent the bounded result from resolving.
      }
      try {
        channel.port1.onmessage = null;
      } catch {
        // A broken port cannot prevent the cleanup report from resolving.
      }
      try {
        channel.port1.close();
      } catch {
        // Transferred or already-closed ports are safe to ignore.
      }
      try {
        channel.port2.close();
      } catch {
        // Transferred or already-closed ports are safe to ignore.
      }
      resolve(result);
    };

    try {
      timeoutId = scheduleTimeout(
        () =>
          finish({
            status: "failed",
            code: "SERVICE_WORKER_TIMEOUT",
          }),
        timeoutMs,
      );
    } catch {
      finish({
        status: "failed",
        code: "SERVICE_WORKER_POST_FAILED",
      });
      return;
    }

    try {
      channel.port1.onmessage = (event) => {
        if (!isResponse(event.data, requestId)) {
          finish({
            status: "failed",
            code: "SERVICE_WORKER_RESPONSE_INVALID",
          });
          return;
        }

        finish(
          event.data.ok
            ? { status: "cleared" }
            : {
                status: "failed",
                code: "CACHE_CLEANUP_FAILED",
              },
        );
      };
      channel.port1.start?.();
      controller.postMessage(
        {
          type: PROTECTED_CACHE_CLEAR_REQUEST,
          version: PROTECTED_CACHE_CLEAR_PROTOCOL_VERSION,
          requestId,
        },
        [channel.port2],
      );
    } catch {
      finish({
        status: "failed",
        code: "SERVICE_WORKER_POST_FAILED",
      });
    }
  });
}
